import { useNavigate } from "react-router-dom";
import { Check, Zap, Star, Sparkles } from "lucide-react";
import { useAuth, userMode } from "@/context/AuthContext";
import NavTop from "@/components/nav/nav_top";
import PromoBanner from "@/components/promo/promo_banner";
import FooterMain from "@/components/footer/footer_main";
import ButtonMain from "@/components/buttons/button_main";
import ButtonGhost from "@/components/buttons/button_ghost";
import { cn } from "@/lib/utils";

// audience: who each plan is FOR. A signed in renter never sees Owner or
// Realtor plans (they cannot buy them anyway); listers never see Renter Plus.
// Guests see everything so the page still works as a marketing overview.
const COLUMNS = [
  {
    name: "Renter Plus", tagline: "Swipe without limits", price: "$9.99", period: "per month",
    features: ["Unlimited swiping (free accounts get 20 swipes a day)", "Unlimited rewinds", "See who liked you first", "Chat is always free when you match"],
    cta: "Go unlimited", product: "renter_plus_monthly", highlight: false, audience: "renter",
  },
  {
    name: "Owners", tagline: "List and fill your place", price: "Free", period: "first listing · then $9 each",
    features: ["Your first listing is free forever", "$9 per extra listing, live until rented", "Swipe through qualified renters", "Provincial lease generation and e sign"],
    cta: "List your place", product: "listing", highlight: true, audience: "lister",
  },
  {
    name: "Pro", tagline: "For realtors and busy owners", price: "$29", period: "per month",
    features: ["See everyone who liked your homes", "Unlimited active listings", "Verified Realtor badge on every listing", "Manage client listings in one dashboard", "Applicant analytics and priority support", "Representative form for owner consent"],
    cta: "Go Pro", product: "pro_monthly", highlight: false, audience: "lister",
  },
];

// Boost is a lister product: it is bought from the lister dashboard for one
// specific listing, so its card is informational here (no checkout link).
const ADDONS = [
  { icon: Zap, name: "Boost", price: "from $4.99", desc: "For listers: move your listing forward. Four tiers — Bump $4.99 (1 day), Spotlight $9.99 (3 days), Featured $19.99 (7 days), Turbo $34.99 (7 days). Start it from your listings dashboard.", checkout: false, audience: "lister" },
  { icon: Star, name: "Super like", price: "$1.99", desc: "Stand out with an amber star the other side sees first.", audience: "renter" },
  { icon: Sparkles, name: "Featured listing", price: "$14.99/wk", desc: "Pinned placement on the map and in search.", audience: "lister" },
  // Trust Package intentionally NOT here: screening is not live yet, and
  // when it launches it sells from the Screening area, not this page.
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user || user.demo;

  // Mode aware: a signed in renter sees only renter plans and extras, a
  // lister only lister ones. Guests get the full overview.
  const mode = isGuest ? null : userMode(user);
  const columns = mode ? COLUMNS.filter((c) => c.audience === mode) : COLUMNS;
  const addons = mode ? ADDONS.filter((a) => a.audience === mode) : ADDONS;
  // With a single plan on show, its button should be the primary one even
  // if it is not the "Most popular" pick of the full grid.
  const soloColumn = columns.length === 1;

  // Guests must log in before any checkout. After auth they land straight on
  // the checkout they asked for.
  function goCheckout(product) {
    const dest = `/checkout/${product}`;
    if (isGuest) navigate("/auth/login", { state: { from: dest } });
    else navigate(dest);
  }

  return (
    <div className="min-h-screen bg-slate-50/70 pb-24 md:pb-0">
      <NavTop />
      <main className="mx-auto max-w-6xl px-4 pt-8 sm:px-6">
        <PromoBanner className="mb-8" />
        <div className="text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-brand-ink sm:text-4xl">Simple pricing, no surprises</h1>
          <p className="mx-auto mt-3 max-w-xl text-base font-semibold text-slate-500">Browsing is free for everyone. Pay only for the extras that speed things up.</p>
        </div>

        <div className={cn("mt-12 grid gap-6", soloColumn ? "mx-auto max-w-md" : "lg:grid-cols-3")}>
          {columns.map((col) => (
            <div key={col.name} className={cn("relative flex flex-col rounded-3xl border bg-white p-8 shadow-sm transition hover:shadow-md", col.highlight ? "border-brand-primary ring-1 ring-brand-primary" : "border-slate-100")}>
              {col.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-primary px-4 py-1 text-xs font-extrabold text-white">Most popular</span>
              )}
              <h2 className="text-lg font-extrabold text-brand-ink">{col.name}</h2>
              <p className="text-sm font-semibold text-slate-500">{col.tagline}</p>
              <p className="mt-5 text-4xl font-extrabold tracking-tight text-brand-ink">{col.price}</p>
              <p className="text-sm font-bold text-slate-400">{col.period}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {col.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm font-semibold text-slate-600">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand-lime" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {col.highlight || soloColumn ? (
                  <ButtonMain full onClick={() => goCheckout(col.product)}>{col.cta}</ButtonMain>
                ) : (
                  <ButtonGhost full onClick={() => goCheckout(col.product)}>{col.cta}</ButtonGhost>
                )}
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-16 text-center text-xl font-extrabold text-brand-ink">One time extras</h2>
        {/* pb-fab-clear: the last CTA card must never end up under the SwipBot FAB on mobile */}
        <div className="mt-6 grid gap-4 pb-fab-clear sm:grid-cols-2 md:pb-16 lg:grid-cols-4">
          {addons.map((a) => {
            const body = (
              <>
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-tint text-brand-lime">
                  <a.icon size={19} />
                </span>
                <p className="mt-3 font-extrabold text-brand-ink">{a.name} <span className="text-brand-lime">{a.price}</span></p>
                <p className="mt-1 text-sm font-semibold leading-snug text-slate-500">{a.desc}</p>
              </>
            );
            if (a.checkout === false) {
              return (
                <div key={a.name} className="rounded-3xl border border-slate-100 bg-white p-6 text-left shadow-sm">
                  {body}
                </div>
              );
            }
            return (
              <button
                key={a.name}
                type="button"
                onClick={() => goCheckout(a.name.toLowerCase().replace(/ /g, "_"))}
                className="rounded-3xl border border-slate-100 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                {body}
              </button>
            );
          })}
        </div>
      </main>
      <FooterMain />
    </div>
  );
}
